import { createModuleContext } from '@rebuild/utils/moduleContext';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useModuleDefaultCover } from '@rebuild/hooks/comp/useModuleDefaultCover';
import { computed } from 'vue';
import { useInvestmentActivityTabs } from '@rebuild/materials/shared/investment_activity/useInvestmentActivityTabs.js';
import i18n from '@/locales';

function buildBaseCtx() {
	return {
		coverListInFlow: [
			require(`@/assets/images/home/activity_icon_01.png`),
			require(`@/assets/images/home/activity_icon_02.png`),
			require(`@/assets/images/home/activity_icon_03.png`)
		],
		useBorder: false,
		params: {},
		formattedOverview: rows => {
			if (!rows?.length) return [];
			return rows.slice(0, 3);
		},
		viewInHK09606: false
	};
}

const { provide: provideCurrentContext, use: useCurrentContext } = createModuleContext(
	'investment_activity',
	buildBaseCtx
);

const useCurrentReactive = () => {
	const context = useCurrentContext();

	const { investment_activity, investment_activity_recently } = useModuleData();
	const isEmpty = computed(() => {
		const { data, empty } = investment_activity;
		return empty || !data?.rows?.length;
	});
	const getActivityList = computed(() => {
		const { data } = investment_activity;
		const { rows } = data || {};
		return context.formattedOverview(rows);
	});
	const loadActivityList = async (businessTypes = []) => {
		return investment_activity.refresh({
			pagestart: 0,
			pagenum: 20,
			businessTypes
		});
	};
    
	const BUSINESS_TYPES = {
		'research': 3,
		'roadshow': 8,
		'offline': 9
	};
	const isEN = computed(() => i18n.locale === 'en');
	const { getAvailableTabs } = useInvestmentActivityTabs();

	const refreshComponent = async (businessTypes = []) => {
		if(isEN.value) {
			await loadActivityList(businessTypes);
			return;
		}
		const options = await getAvailableTabs();
		if (!options?.length) {
			investment_activity.loading = false;
			return;
		} else {
			let allTab = options.find(item => item.value === 'investment_activity_all');
			let type = allTab ? [] : options.map(item => BUSINESS_TYPES[item.value]);
			await loadActivityList(type);
		}
	};

	const { viewModule } = useModuleNavigation();
	const viewActivity = item => {
		if (item.businessTypes === 8 && context.viewInHK09606 && skipInHK09606(item)) {
			return;
		}
		viewModule('investment_activity', item);
	};
	const viewRoadshow = item => {
		if (context.viewInHK09606 && skipInHK09606(item)) {
			return;
		}
		viewModule('roadshow', item);
	};

	const skipInHK09606 = item => {
		if (item.conferenceStatus === 7 && item.recordSupport !== 1) {
			// 已经结束且没有保留回放，不允许点击进入详情页；
			return true;
		}
		return false;
	};

	const { getDefaultCover } = useModuleDefaultCover();
	const formatter = item => ({
		image: item.logo169 || item.logo43 || getDefaultCover('investment_activity_169'),
		time: item.date,
		value: item.title,
	});

	const activityList = computed(() => {
		const { data } = investment_activity;
		const { rows } = data || {};
		return rows;
	});
	const loadActivityListRecently = (businessTypes) => {
		return investment_activity_recently.refresh({
			pagestart: 0,
			pagenum: 5,
			businessTypes: businessTypes || [8, 9]
		});
	};
	const activityListRecently = computed(() => {
		const { data } = investment_activity_recently;
		const { rows } = data || {};
		return rows;
	});
	
	return {
		investment_activity,
		isEmpty,
		getActivityList,
		loadActivityList,
		viewActivity,
		viewRoadshow,
		formatter,
		activityList,
		loadActivityListRecently,
		activityListRecently,
		skipInHK09606,
		refreshComponent,
		BUSINESS_TYPES
	};
};

export { provideCurrentContext, useCurrentContext, useCurrentReactive };
